/**
 * @file BackupSummary.tsx
 * @description Сводка бэкапов (phase08.9; phase19.26): плитки «Статус /
 * Последний успех / За 30 дней / Ошибок», полоса dead-man и текст последней
 * ошибки.
 *
 * Полоса здоровья — `backupHealthNotice`: выключенный бэкап — нейтральная
 * подсказка, включённый без успеха > 36 ч — предупреждение. Плитки 2×2 до lg
 * и в ряд с lg (шаг 7.1: на 768 четыре в ряд резали подписи и значения);
 * длинные значения переносятся, а не обрезаются.
 */

import { AlertCircle, CheckCircle2, Clock, Database } from "lucide-react";
import type { ReactNode } from "react";

import { EditorialCard } from "../../ui/EditorialCard";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SectionLabel } from "../../ui/SectionLabel";
import { backupHealthNotice, type BackupConfigDto } from "../../../lib/api-backups";
import { cn } from "@/lib/utils";

interface Props {
  config: BackupConfigDto;
}

export function BackupSummary({ config }: Props) {
  const notice = backupHealthNotice(config);
  const lastSuccess = config.lastSuccessAt
    ? new Date(config.lastSuccessAt).toLocaleString("ru-RU", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "Не было";

  return (
    <div className="flex flex-col gap-3">
      {notice ? <NoticeStrip tone={notice.tone}>{notice.text}</NoticeStrip> : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          icon={<Database className="size-4" />}
          label="Статус"
          value={config.isEnabled ? "Включено" : "Выключено"}
          tone={config.isEnabled ? "ok" : "muted"}
        />
        <KpiTile
          icon={<CheckCircle2 className="size-4" />}
          label="Последний успех"
          value={lastSuccess}
          // Дата и время — длинное значение: роль заголовка секции, не показателя.
          textual={Boolean(config.lastSuccessAt)}
          tone={config.isEnabled && config.stale ? "warning" : config.lastSuccessAt ? "ok" : "muted"}
        />
        <KpiTile
          icon={<Clock className="size-4" />}
          label="За 30 дней"
          value={`${config.stats.successLast30Days} из ${config.stats.runsLast30Days}`}
        />
        <KpiTile
          icon={<AlertCircle className="size-4" />}
          label="Ошибок (30 дней)"
          value={String(config.stats.failuresLast30Days)}
          tone={config.stats.failuresLast30Days > 0 ? "error" : "ok"}
        />
      </div>

      {config.lastRunStatus === "FAILED" && config.lastErrorMessage ? (
        <NoticeStrip tone="error">
          Последний запуск завершился ошибкой: {config.lastErrorMessage}
        </NoticeStrip>
      ) : null}
    </div>
  );
}

type Tone = "ok" | "warning" | "error" | "muted";

/** Цвет значения плитки — реестром, не тернарником. */
const TONE_CLASS: Record<Tone, string> = {
  ok: "text-on-surface",
  warning: "text-warning",
  error: "text-destructive",
  muted: "text-on-surface-variant",
};

function KpiTile({
  icon,
  label,
  value,
  tone = "ok",
  textual = false,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  tone?: Tone;
  /** Длинное текстовое значение — роль заголовка секции вместо показателя. */
  textual?: boolean;
}) {
  return (
    <EditorialCard>
      <EditorialCard.Body>
        <div className="flex items-start gap-2 text-on-surface-variant">
          <span className="mt-px shrink-0">{icon}</span>
          <SectionLabel className="break-words">{label}</SectionLabel>
        </div>
        <div className={cn(textual ? "type-section-title" : "type-metric", "mt-2 break-words", TONE_CLASS[tone])}>
          {value}
        </div>
      </EditorialCard.Body>
    </EditorialCard>
  );
}
